import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { amplifyConfig } from './config/amplify-config';
import LandingPage from './components/LandingPage';
import RequestAccessPage from './components/RequestAccessPage';
import OpportunityForm from './components/OpportunityForm';
import OpportunityList from './components/OpportunityList';
import OpportunityDetail from './components/OpportunityDetail';
import AdminDashboard from './components/AdminDashboard';
import CKDashboard from './components/CKDashboard';
import UserManagement from './components/UserManagement';
import PartnerDashboard from './components/PartnerDashboard';
import PipelineView from './components/PipelineView';
import ManagerApprovals from './components/ManagerApprovals';
import ManageTeam from './components/ManageTeam';
import ReleaseNotes from './components/ReleaseNotes';
import './App.css';

Amplify.configure(amplifyConfig);

function CKLogo() {
  return (
    <img
      src="https://www.cloud-kinetics.com/wp-content/uploads/2023/07/CloudKinetics-Logo.svg"
      alt="Cloud Kinetics"
      className="ck-logo"
    />
  );
}

function AppShell() {
  const { signOut, user } = useAuthenticator();
  const [ckAdmin, setCkAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    fetchAuthSession().then(session => {
      const payload = session.tokens?.idToken?.payload;
      const groups = payload?.['cognito:groups'] || [];
      const isAdmin = Array.isArray(groups)
        ? groups.includes('CKAdmin')
        : groups === 'CKAdmin';
      setCkAdmin(isAdmin);
      setUserRole(payload?.['custom:role'] || 'partner_admin');
    }).catch(() => {
      setCkAdmin(false);
      setUserRole(null);
    });
  }, [user]);

  return (
    <div className="app">
      <header className="ck-header">
        <div className="ck-header-inner">
          <div className="ck-header-brand">
            <CKLogo />
            <span className="ck-brand-sub">Partner Network</span>
          </div>
          <div className="ck-user-info">
            <span className="ck-user-email">{user?.signInDetails?.loginId}</span>
            {ckAdmin && <span className="ck-role-badge">CK Admin</span>}
            <button className="ck-signout-btn" onClick={signOut}>Sign Out</button>
          </div>
        </div>
      </header>

      <nav className="ck-nav">
        <div className="ck-nav-inner">
          {ckAdmin ? (
            <>
              <NavLink to="/ck-dashboard" className={({ isActive }) => isActive ? 'active' : ''}>Dashboard</NavLink>
              <NavLink to="/pipeline" className={({ isActive }) => isActive ? 'active' : ''}>Pipeline</NavLink>
              <NavLink to="/admin" className={({ isActive }) => isActive ? 'active' : ''}>All Deals</NavLink>
              <NavLink to="/users" className={({ isActive }) => isActive ? 'active' : ''}>Users</NavLink>
              <NavLink to="/release-notes" className={({ isActive }) => isActive ? 'active' : ''}>Release Notes</NavLink>
            </>
          ) : (
            <>
              <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'active' : ''}>Dashboard</NavLink>
              <NavLink to="/pipeline" className={({ isActive }) => isActive ? 'active' : ''}>Pipeline</NavLink>
              {userRole === 'manager' && (
                <>
                  <NavLink to="/approvals" className={({ isActive }) => isActive ? 'active' : ''}>Approvals</NavLink>
                  <NavLink to="/team" className={({ isActive }) => isActive ? 'active' : ''}>Team</NavLink>
                </>
              )}
              <NavLink to="/opportunities" className={({ isActive }) => isActive ? 'active' : ''}>My Deals</NavLink>
              <NavLink to="/submit" className={({ isActive }) => isActive ? 'active' : ''}>+ New Deal</NavLink>
            </>
          )}
        </div>
      </nav>

      <main>
        <Routes>
          {ckAdmin ? (
            <>
              <Route path="/ck-dashboard" element={<CKDashboard />} />
              <Route path="/pipeline" element={<PipelineView isCKAdmin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/opportunities/:id" element={<OpportunityDetail isCKAdmin />} />
              <Route path="/users" element={<UserManagement />} />
              <Route path="/release-notes" element={<ReleaseNotes />} />
              <Route path="*" element={<Navigate to="/ck-dashboard" />} />
            </>
          ) : (
            <>
              <Route path="/dashboard" element={<PartnerDashboard />} />
              <Route path="/pipeline" element={<PipelineView />} />
              {userRole === 'manager' && <Route path="/approvals" element={<ManagerApprovals />} />}
              {userRole === 'manager' && <Route path="/team" element={<ManageTeam />} />}
              <Route path="/submit" element={<OpportunityForm />} />
              <Route path="/opportunities" element={<OpportunityList />} />
              <Route path="/opportunities/:id" element={<OpportunityDetail />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </>
          )}
        </Routes>
      </main>

      <footer className="ck-footer">
        <p>&copy; {new Date().getFullYear()} Cloud Kinetics Pte Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Authenticator.Provider>
        <RootRoutes />
      </Authenticator.Provider>
    </Router>
  );
}

function RootRoutes() {
  const { authStatus } = useAuthenticator(ctx => [ctx.authStatus]);

  if (authStatus === 'authenticated') {
    return (
      <Routes>
        <Route path="/request-access" element={<RequestAccessPage />} />
        <Route path="*" element={<AppShell />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/request-access" element={<RequestAccessPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function LoginRoute() {
  return (
    <div className="auth-page">
      <div className="auth-card auth-card--login">
        <div className="auth-card-header">
          <a href="/" className="auth-back">← Back to home</a>
          <div className="auth-brand">
            <img
              src="https://www.cloud-kinetics.com/wp-content/uploads/2023/07/CloudKinetics-Logo.svg"
              alt="Cloud Kinetics"
              className="auth-ck-logo"
            />
          </div>
          <h2 className="auth-title">Partner Sign In</h2>
          <p className="auth-subtitle">Access is by invitation only. <a href="/request-access">Request access →</a></p>
        </div>
        <Authenticator hideSignUp />
      </div>
    </div>
  );
}
